import React, { Fragment } from 'react';

import t from '../../../labels/labelling-utils';
import { DRIVERS_RANKING_POINTS, DriversRankingVariants } from '../../../typings/drivers-ranking';
import {
  RACE_STATUS_PLAYED,
  RACE_STATUS_PLAYING,
  RaceSessionStatus,
  RaceSessionType,
} from '../../../typings/race-session';
import { getSessionAccordionLabels } from '../../../utils/motorsports';
import { isPlayed, isQualifyingPhase } from '../../../utils/race-status';
import { makeClassNames } from '../../../utils/styles';
import { Text } from '../../atoms';
import ErrorComponent from '../../atoms/error/error';
import IconArrowUp from '../../atoms/icons/icon-arrow-up';
import ResultsTitle from '../../atoms/results-title/results-title';
import Spacer from '../../atoms/spacer/spacer';
import StaticLinksContainer from '../../molecules/static-links-container/static-links-container';
import Accordion, { IAccordionTitle } from '../../organisms/accordion/accordion';
import TopDriversTable, { ITopDriversTable } from '../../organisms/top-drivers-ranking-table/top-drivers-ranking-table';

import './driver-standings.scss';

import { CourtesyMessage, GrandPrixTitle, UpdateComponent } from './drivers-standings-components';
import {
  getAccordionName,
  getNotPlayedStandingListRows,
  getSEOTitle,
  getSingleTable,
  getStandingsTitle,
  getTopDriversTableProps,
  hasPlayedSession,
  isSingleRankingTable,
  isValidStandingsList,
} from './drivers-standings-helpers';

export interface IDriverStandings {
  hasSEOTitle?: boolean; // If hasTitle is false is lgp variant
  hasLgpAnalytics?: boolean;
  competitionName?: string;
  lastGrandPrix?: string;
  grandPrixTitle?: string;
  standingsList: ITopDriversTable[];
  rankingVariant?: DriversRankingVariants;
  status?: RaceSessionStatus; // This is NOT for controling single table's status.
  showTitle?: boolean; // To force-show H2 title in rankingVariant !== total-points
  links?: { text: string; link: string }[];
  sessionName?: RaceSessionType;
}

const classNames = () =>
  makeClassNames({
    topDrivers: 'top-drivers',
    seoTitle: 'seo-title',
  });

const getAccordionTitle = (tableTitle: string): string =>
  (getSessionAccordionLabels()[tableTitle] ?? tableTitle).toLocaleUpperCase();

const DriverStandings = ({
  /* istanbul ignore next: default value */ competitionName = '',
  /* istanbul ignore next: default value */ rankingVariant = DRIVERS_RANKING_POINTS,
  standingsList,
  lastGrandPrix,
  grandPrixTitle,
  /* istanbul ignore next: default value */ showTitle = false,
  /* istanbul ignore next: default value */ hasSEOTitle = false,
  /* istanbul ignore next: default value */ hasLgpAnalytics = false,
  links,
  sessionName = 'FP2',
}: IDriverStandings) => {
  const cn = classNames();
  if (!isValidStandingsList(standingsList)) {
    return <ErrorComponent>{t('unavailable.data')}</ErrorComponent>;
  }
  const isSingleTable = isSingleRankingTable(rankingVariant, standingsList);
  const singleTable = getSingleTable(rankingVariant, standingsList);
  return (
    <div className={cn.topDrivers}>
      <Fragment>
        {!hasSEOTitle ? (
          <Fragment>
            <GrandPrixTitle>{grandPrixTitle}</GrandPrixTitle>
            <ResultsTitle
              title={getStandingsTitle(rankingVariant, competitionName, showTitle)}
              update={
                <UpdateComponent
                  rankingVariant={rankingVariant}
                  lastGrandPrix={lastGrandPrix}
                  status={singleTable.status}
                  standingsList={standingsList}
                />
              }
              titleFontSize="14--16"
              useUpdatePrefix={false}
            />
          </Fragment>
        ) : (
          <Text tag="h1" fontSize="32--34" color="blue" className={cn.seoTitle}>
            {sessionName && getSEOTitle(sessionName)}
          </Text>
        )}
        {isSingleTable ? (
          // single Ranking Table
          <Fragment>
            {hasSEOTitle && <Spacer size={30} />}
            {/* Print Error message and empty table head if NOT isPlayed */}
            {singleTable.status && !isPlayed(singleTable.status) ? (
              <Fragment>
                <TopDriversTable
                  {...getTopDriversTableProps()}
                  rows={getNotPlayedStandingListRows(standingsList)}
                  rankingVariant={rankingVariant}
                  hasLgpAnalytics={hasLgpAnalytics}
                  showTimings={singleTable.showTimings}
                />
                <CourtesyMessage
                  sessionStatus={singleTable.status}
                  rankingVariant={rankingVariant}
                  date={singleTable.startDateTime}
                  fontSize="18--22"
                  dataSpaced
                />
              </Fragment>
            ) : (
              <TopDriversTable
                {...getTopDriversTableProps()}
                rows={singleTable.rows}
                rankingVariant={rankingVariant}
                hasLgpAnalytics={hasLgpAnalytics}
                trackingData={singleTable.trackingData || {}}
              />
            )}
          </Fragment>
        ) : null}
        {!isSingleTable &&
        isQualifyingPhase(rankingVariant) &&
        hasPlayedSession(standingsList) &&
        standingsList.length > 1
          ? // Multiple Accordion Management E.g: Q1 Q2 Q3 for qualifying
            standingsList.map((topDriversTable: ITopDriversTable, index: number) => {
              const rows = topDriversTable.rows;
              const trackingData = topDriversTable.trackingData;
              const statusSession = topDriversTable.status;
              const accordionTitle: IAccordionTitle = {
                /* istanbul ignore next: optional chaining not supported */ text: getAccordionTitle(
                  topDriversTable?.tableTitle || ''
                ),
                color: 'blue',
                fontSize: '16',
                fontWeight: '500',
              };
              const accordionName = getAccordionName(accordionTitle);
              return statusSession === RACE_STATUS_PLAYED || statusSession === RACE_STATUS_PLAYING ? (
                <Fragment key={accordionName}>
                  <Spacer size={30} />
                  <Accordion
                    isOpen={!index}
                    id={`${accordionName}-${index}`}
                    title={accordionTitle}
                    arrow={<IconArrowUp size="xxsmall" color="inherit" />}
                    variant="gray-background"
                  >
                    <TopDriversTable
                      {...getTopDriversTableProps(index)}
                      rows={rows}
                      rankingVariant={rankingVariant}
                      hasLgpAnalytics={hasLgpAnalytics}
                      trackingData={trackingData || {}}
                      showTimings={topDriversTable.showTimings}
                    />
                    {statusSession === RACE_STATUS_PLAYING ? (
                      <CourtesyMessage
                        sessionStatus={RACE_STATUS_PLAYING}
                        rankingVariant={rankingVariant}
                        date={topDriversTable.startDateTime}
                        fontSize="18--22"
                        dataSpaced
                      />
                    ) : null}
                  </Accordion>
                  <Spacer size={30} />
                </Fragment>
              ) : null;
            })
          : null}

        {links && links.length && <StaticLinksContainer links={links} />}
      </Fragment>
    </div>
  );
};

export default DriverStandings;
